import { Controller } from '@hotwired/stimulus'
import * as analytics from '../libs/analytics'

// Teachers who teach from the preview give themselves away by zooming the
// slides up for the room. Every zoom in past 100% opens a modal pointing at
// Present mode instead. Arrow and page keys are left to the carousel: stepping
// through slides is preparing too.
const ZOOM_IN_KEYS = ['+', '=']
// Side panels (devtools, Arc's sidebar) also shrink the page, so an open panel
// can read as arriving zoomed; a 110% zoom is still worth catching.
const ZOOMED_ON_LOAD = 1.1

export default class extends Controller {
  static targets = ['modal', 'slides']
  static values = { lessonId: Number, schoolClassId: Number }

  connect() {
    this.keydown = this.keydown.bind(this)
    this.wheel = this.wheel.bind(this)
    this.zoomChanged = this.zoomChanged.bind(this)
    this.pinched = this.pinched.bind(this)

    document.addEventListener('keydown', this.keydown)
    document.addEventListener('wheel', this.wheel, { passive: false })
    window.visualViewport?.addEventListener('resize', this.pinched)

    // Chrome remembers zoom per site, so a teacher who zoomed once arrives
    // zoomed. Outer width ignores page zoom while inner width doesn't.
    const arrived = window.outerWidth / window.innerWidth
    const zoomedOnLoad = arrived >= ZOOMED_ON_LOAD
    this.unzoomed = window.devicePixelRatio / (zoomedOnLoad ? arrived : 1)
    this.holdSlideSize()
    this.watchZoom()

    if (zoomedOnLoad) this.open('zoom_on_load')
  }

  disconnect() {
    document.removeEventListener('keydown', this.keydown)
    document.removeEventListener('wheel', this.wheel)
    window.visualViewport?.removeEventListener('resize', this.pinched)
    this.media.removeEventListener('change', this.zoomChanged)
    this.prompt?.remove()
  }

  keydown(event) {
    if (!(event.ctrlKey || event.metaKey) || !ZOOM_IN_KEYS.includes(event.key)) return
    if (this.zoomLevel < 1) return

    event.preventDefault()
    this.open('zoom_shortcut')
  }

  // Ctrl + wheel is how browsers report both Ctrl-scroll and trackpad pinch.
  // Only zooming in past 100% is blocked: a teacher who zoomed out (or in, some
  // other way) can always get back to 100%.
  wheel(event) {
    if (!event.ctrlKey || event.deltaY >= 0 || this.zoomLevel < 1) return

    event.preventDefault()
    this.open('pinch')
  }

  // Zoom changes have no event of their own; each change re-resolves the
  // devicePixelRatio media query, firing `change` once, so re-arm every time.
  watchZoom() {
    this.media = window.matchMedia(`(resolution: ${window.devicePixelRatio}dppx)`)
    this.media.addEventListener('change', this.zoomChanged, { once: true })
  }

  zoomChanged() {
    if (this.zoomLevel > 1) this.open('zoom')
    this.holdSlideSize()
    this.watchZoom()
  }

  // Zoom that gets past us (the browser menu, arriving zoomed) enlarges the
  // page text as usual, but the slides shrink back to their 100% size.
  holdSlideSize() {
    if (!this.hasSlidesTarget) return

    this.slidesTarget.style.zoom = this.zoomLevel > 1 ? 1 / this.zoomLevel : ''
  }

  // 1 is 100%. Rounded so floating-point noise in devicePixelRatio can't make
  // 100% read as just above or below it.
  get zoomLevel() {
    return Math.round((window.devicePixelRatio / this.unzoomed) * 100) / 100
  }

  pinched() {
    if (window.visualViewport.scale > 1) this.open('pinch')
  }

  open(trigger) {
    if (this.prompt?.isConnected) return

    this.prompt = this.modalTarget.content.firstElementChild.cloneNode(true)
    this.prompt.dataset.turboTemporary = ''
    document.body.append(this.prompt)
    analytics.capture('teaching_prompt_shown', {
      trigger,
      lesson_id: this.lessonIdValue,
      school_class_id: this.schoolClassIdValue || undefined
    })
  }
}
